import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useSession } from '@/stores/session';
import { getRealtimeToken, getSubscribeToken } from './api';
import { RealtimeManager } from './manager';
import { RealtimeContext, type RealtimeContextValue } from './realtimeContext';
import type { PublicationHandler, RealtimeStatus, Unsubscribe } from './types';

/**
 * Owns the app's single realtime connection (FE-ADR-017).
 *
 * Connects only when the caller is authenticated **in a school** — the
 * connection token is per-membership. `activeSchoolSlug` is in the effect's
 * dependencies on purpose: switching school tears the socket down and builds a
 * new one, so a connection authorized for one tenant can never carry another
 * tenant's traffic.
 *
 * With `VITE_CENTRIFUGO_URL` unset the provider mounts, reports `disabled`, and
 * does nothing else — no client, no requests, no listeners.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const url = import.meta.env.VITE_CENTRIFUGO_URL ?? '';
  const isAuthenticated = useSession(s => s.isAuthenticated);
  const activeSchoolSlug = useSession(s => s.activeSchoolSlug);

  const [status, setStatus] = useState<RealtimeStatus>(url ? 'disconnected' : 'disabled');

  // One manager for the app's lifetime — the socket inside it is what starts and
  // stops. `url` comes from the build-time env and never changes at runtime, so
  // the lazy initializer runs exactly once.
  const [manager] = useState<RealtimeManager | null>(() =>
    url
      ? new RealtimeManager({
          url,
          getConnectionToken: async () => (await getRealtimeToken()).token,
          getSubscriptionToken: async channel => (await getSubscribeToken(channel)).token,
          onStatus: setStatus,
        })
      : null
  );

  useEffect(() => {
    if (!manager || !isAuthenticated || !activeSchoolSlug) return;
    manager.start();
    return () => manager.stop();
  }, [manager, isAuthenticated, activeSchoolSlug]);

  // Stable identity: `useRealtimeChannel` lists it as an effect dependency, and
  // a changing `subscribe` would resubscribe on every render.
  const subscribe = useCallback(
    <T,>(channel: string, handler: PublicationHandler<T>): Unsubscribe =>
      manager?.subscribe(channel, handler) ?? (() => {}),
    [manager]
  );

  const value = useMemo<RealtimeContextValue>(
    () => ({ status: url ? status : 'disabled', subscribe }),
    [url, status, subscribe]
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}
